// ============================================================
// TOKENS - where each combatant is standing.
//
// One document per token, so two people dragging different tokens merge
// server-side rather than racing over one blob. A token carries a `rev`
// Lamport clock for exactly the reason `combatants/*.state` does: with
// many clients writing at high frequency, YOUR OWN LATE ECHO is
// otherwise indistinguishable from someone else's change.
//
// Colour is NOT stored. `actorId` and `targetId` are local by design, so
// every browser renders the same shared token list with its own
// highlighting - no per-player documents, no new architecture.
//
// Pure. Zero React dependency.
// ============================================================
import type { Vec3 } from './types';
import type { Boundary } from './boundary';
import { clampToBoundary } from './boundary';
import { heightForSize, eyeHeightForSize } from './coverModel';
import { sameValue } from '../canonicalJson';

/** How tall a combatant's eyes are, in metres, for line-of-sight. */
export const DEFAULT_EYE_HEIGHT = 1.6;
/** How much ground a combatant occupies, as a radius in metres. */
const DEFAULT_FOOTPRINT = 0.4;

export interface Token {
  /** The combatant's id. A token IS a combatant's position. */
  id: string;
  kind: 'token';
  /** Ground position, in metres. Floats - never snapped to a tile. */
  x: number;
  z: number;
  /** How high the ground is under them: a rooftop, a crate, a gantry. */
  elevation: number;
  /** Metres from their feet to their eyes. Size matters for cover. */
  eyeHeight: number;
  /**
   * The combatant's full height in metres - what cover is measured
   * against. Absent on tokens written before v7.17.0, where callers fall
   * back to a human.
   */
  height?: number;
  footprint: number;
  /**
   * Terrain does not adjudicate this combatant: no cover from it, no
   * line of sight blocked by it. For anything flying, ethereal, or
   * simply outside what the geometry should be deciding.
   */
  ignoresTerrain?: boolean;
  /**
   * Which way a hull points - degrees, 0 = due east, matching
   * `Math.atan2(z, x)`. **Vehicles only, and absent until one is turned.**
   *
   * A heading was deleted wholesale in v8.29.0 because it was computed
   * for every token, synced, tested - and read by nothing. This one has
   * exactly one reader and it is in the attack chain: `arcFor` in
   * `facing.ts` decides which of a vehicle's three armour values a shot
   * meets. That is the difference, and the ADR that removed the old
   * field named this as the sanctioned way to bring one back.
   *
   * Absent means "nobody has turned this hull", which the facing
   * derivation reports as no answer rather than guessing north.
   */
  heading?: number;
  /** Lamport clock. Bumped on LOCAL MUTATION, never on send. */
  rev: number;
}

export function makeToken(id: string, overrides: Partial<Token> = {}): Token {
  return {
    id,
    kind: 'token',
    x: 0,
    z: 0,
    elevation: 0,
    eyeHeight: DEFAULT_EYE_HEIGHT,
    footprint: DEFAULT_FOOTPRINT,
    rev: 0,
    ...overrides,
  };
}

/**
 * A token sized from the combatant's Size trait.
 *
 * Height and eye height both follow from it, so a Dreadnought looks over
 * the wall a guardsman hides behind without anyone typing a number. The
 * footprint grows too, but far more slowly than the height - a Massive
 * creature is tall out of all proportion to the ground it stands on.
 */
export function tokenForSize(id: string, size: string | undefined, overrides: Partial<Token> = {}): Token {
  const height = heightForSize(size);
  return makeToken(id, {
    height,
    eyeHeight: eyeHeightForSize(size),
    footprint: DEFAULT_FOOTPRINT * Math.max(0.5, Math.cbrt(height / 1.8)),
    ...overrides,
  });
}

/**
 * Turn a hull to point along `heading` (degrees, 0 = due east).
 *
 * Restored from the v8.29.0 deletion unchanged - it was six correct
 * lines - and now has the reader it always lacked.
 */
export function turnToken(token: Token, heading: number): Token {
  return { ...token, heading: ((heading % 360) + 360) % 360, rev: token.rev + 1 };
}

/** The height cover is measured against. Falls back to a human. */
export function tokenHeight(token: Pick<Token, 'height' | 'eyeHeight'>): number {
  return token.height ?? token.eyeHeight / 0.9;
}

/** Where the token's feet are. */
export function tokenFoot(token: Pick<Token, 'x' | 'z' | 'elevation'>): Vec3 {
  return { x: token.x, y: token.elevation, z: token.z };
}

/**
 * Where the token's EYES are - the point every sight line starts and
 * ends at. Cover depends on this and not on the feet: a shot that clears
 * a barricade at head height still clears it.
 */
export function tokenEye(token: Token): Vec3 {
  return { x: token.x, y: token.elevation + token.eyeHeight, z: token.z };
}

/**
 * Move a token, clamped into the arena, with its clock advanced.
 *
 * The rev bump lives HERE rather than at the call site so that no path
 * can move a token and forget it - a token whose rev did not advance
 * loses the next merge and snaps back under the player's cursor.
 */
export function moveToken(token: Token, x: number, z: number, boundary: Boundary): Token {
  const p = clampToBoundary(boundary, x, z);
  return { ...token, x: p.x, z: p.z, rev: token.rev + 1 };
}

/** Raise or lower a token onto something. Never below the ground. */
export function raiseToken(token: Token, elevation: number): Token {
  return { ...token, elevation: Math.max(0, elevation), rev: token.rev + 1 };
}

/**
 * HOW FAR A TABLE-WIDE PLACEMENT JUMPS THE REV CLOCK (2026-10-21). A cut's ring, a traveller walking on to a mark and
 * a redeploy on map load put people somewhere ON PURPOSE, for every screen at once - and a drag a player has in
 * flight on that token bumps its rev on EVERY pointer move (`moveToken`), so "one above what was held" loses to a
 * five-step drag and the token lands back where the drag left it. A jump no drag in flight can reach settles it;
 * a drag made AFTER the placement arrives starts from the placement's rev and wins, as a deliberate move should.
 * Returns a rev above `rev` and above any drag in flight on it.
 */
export const outranking = (rev: number): number => rev + 1000;

/**
 * Accept a remote token, or keep the local one.
 *
 * The rev clock is applied as a VETO, exactly as `roster/rev.ts` does:
 * an incoming document with a lower rev is a stale echo of something we
 * have already moved past, and taking it would yank the token backwards
 * mid-drag.
 *
 * THE SAME TOKEN AGAIN KEEPS ITS OBJECT (2026-10-31). Every snapshot
 * re-parses the whole collection - pings live in it too - and a new object
 * for an unchanged marker recomputed everything keyed on it. Compared by
 * value: the server hands keys back in its own order.
 */
export function acceptRemoteToken(local: Token | undefined, remote: Token): Token {
  if (!local) return remote;
  if (remote.rev < local.rev) return local;
  return sameValue(local, remote) ? local : remote;
}

/** The same ids, each holding the very same object. */
function sameTokens(a: Record<string, Token>, b: Record<string, Token>): boolean {
  const ids = Object.keys(a);
  return ids.length === Object.keys(b).length && ids.every((id) => a[id] === b[id]);
}

export interface MergeOptions {
  /**
   * Whether this snapshot is the whole truth.
   *
   * A SNAPSHOT TAKEN MID-BURST IS NOT. Firestore delivers one per write
   * with the rest still in flight, so deploying three at once arrives as
   * [C], then [B,C], then [A,B,C] - and rebuilding the map from each in
   * turn made the squad flicker 3 → 1 → 2 → 3. Usually it settled; when
   * a partial view landed last, the missing two never came back and half
   * a deployed squad was simply gone (v8.22.0).
   *
   * `hasPendingWrites` is exactly this question, which is why the caller
   * passes its negation: a snapshot with nothing outstanding lists every
   * token there is, and one with writes pending lists only some.
   */
  authoritative?: boolean;
  /** Ids the snapshot reported as REMOVED. Always obeyed. */
  removed?: string[];
}

/**
 * Merge a remote snapshot into the local token map, rev by rev.
 *
 * Absent from `remote` means one of two things, and they are not the
 * same: somebody deleted it, or our own write has not surfaced yet. A
 * deletion is announced - `docChanges()` names it - so that is what
 * `removed` carries, and everything else survives a partial view.
 */
export function mergeTokens(
  local: Record<string, Token>,
  remote: Token[],
  { authoritative = true, removed = [] }: MergeOptions = {},
): Record<string, Token> {
  const next: Record<string, Token> = authoritative ? {} : { ...local };
  for (const id of removed) delete next[id];
  for (const token of remote) next[token.id] = acceptRemoteToken(local[token.id], token);
  // Nothing changed: the map itself, so its holder can tell (`useBattlefieldSync` re-renders nothing).
  return sameTokens(next, local) ? local : next;
}

/**
 * How wide a horde stands on the ground, in metres.
 *
 * Radius by the SQUARE ROOT of magnitude, so the AREA is proportional to
 * the bodies in it - thirty gaunts cover thirty gaunts' worth of ground,
 * not thirty times the width of one. It shrinks as the horde is ground
 * down, which is the whole point: the map should show a mob thinning.
 *
 * A single body's share of ground is the token's own footprint, so a
 * horde of one is exactly the marker that is already there.
 */
export function hordeRadius(magnitude: number, footprint = DEFAULT_FOOTPRINT): number {
  if (!(magnitude > 0)) return 0;
  return footprint * Math.sqrt(magnitude);
}

/**
 * A token moved to a spot: same body, new feet - a spot's y, else where it stands now. Hypothetical (the cover cue's
 * spots, a move's two ends), so not `moveToken`, which clamps to the map and bumps the revision.
 */
export function standingAt(token: Token, spot: { x: number; z: number; y?: number }): Token {
  return { ...token, x: spot.x, z: spot.z, elevation: spot.y ?? token.elevation };
}
